// src/services/assets/workflowStates.js

/**
 * =====================================================
 * WORKFLOW STATES
 * =====================================================
 *
 * PURPOSE:
 * - Single source of truth for all workflow states
 * - Used by assetTransactionService, validateWorkflowTransition,
 *   PendingTransfers, ReceiveReturns, assetLedgerService,
 *   assetReplayService
 *
 * RULES:
 * - Never use raw strings anywhere in codebase
 * - Always import from here
 * - Never add states without updating validateWorkflowTransition
 * =====================================================
 */

export const WORKFLOW_STATES = Object.freeze({

    // ─────────────────────────────────────────
    // ACTIVE STATES
    // Transaction is live and awaiting action
    // ─────────────────────────────────────────

    /**
     * Receiver must accept before custody changes
     * Flows: STAFF→STAFF, SITE→STAFF, STAFF→SITE
     */
    PENDING_ACCEPTANCE: "PENDING_ACCEPTANCE",

    /**
     * Return submitted — store must receive physically
     * Flows: STAFF→STORE, SITE→STORE
     */
    RETURN_PENDING: "RETURN_PENDING",

    // ─────────────────────────────────────────
    // TERMINAL STATES
    // Transaction is closed — no further transitions
    // ─────────────────────────────────────────

    /**
     * Custody successfully transferred or return received
     * Flows: All finalized movements
     */
    FINALIZED: "FINALIZED",

    /**
     * Transfer or return rejected by receiver/store
     */
    REJECTED: "REJECTED",

    /**
     * Transfer cancelled by requester before finalization
     */
    CANCELLED: "CANCELLED",
});

// ─────────────────────────────────────────────────
// TERMINAL STATES SET
// Use this for quick terminal check
// ─────────────────────────────────────────────────

export const TERMINAL_WORKFLOW_STATES = Object.freeze([
    WORKFLOW_STATES.FINALIZED,
    WORKFLOW_STATES.REJECTED,
    WORKFLOW_STATES.CANCELLED,
]);

// ─────────────────────────────────────────────────
// ACTIVE PENDING STATES SET
// Use this for duplicate pending detection
// ─────────────────────────────────────────────────

export const ACTIVE_PENDING_WORKFLOW_STATES = Object.freeze([
    WORKFLOW_STATES.PENDING_ACCEPTANCE,
    WORKFLOW_STATES.RETURN_PENDING,
]);

// ─────────────────────────────────────────────────
// HELPER
// ─────────────────────────────────────────────────

export const isTerminalWorkflowState = (state) =>
    TERMINAL_WORKFLOW_STATES.includes(state);

export const isActivePendingWorkflowState = (state) =>
    ACTIVE_PENDING_WORKFLOW_STATES.includes(state);